(() => {
  if (customElements.get('lv-gallery-navigation')) return;

  class LakeviewGalleryNavigation extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSlideChanged = this.onSlideChanged.bind(this);
      this.scheduleRefresh = this.scheduleRefresh.bind(this);
    }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('keydown', this.onKeydown, { signal });
      document.addEventListener('DOMContentLoaded', this.scheduleRefresh, { signal });
      this.scheduleRefresh();

      // A section can connect before Dawn has upgraded its gallery elements.
      if (this.dataset.mode !== 'quote') {
        Promise.all([
          customElements.whenDefined('media-gallery'),
          customElements.whenDefined('slider-component'),
        ]).then(() => {
          if (!signal.aborted) this.scheduleRefresh();
        });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.observer?.disconnect();
      this.observer = null;
      this.viewer?.removeEventListener('slideChanged', this.onSlideChanged);
      this.removeReadyMarker();
      this.gallery = null;
      this.viewer = null;
      this.currentId = null;
      this.lastActiveId = null;
      this.lastThumbnailId = null;
      this.entries = [];
      this.ready = false;
      this.hidden = true;
      this.removeAttribute('data-ready');
    }

    scheduleRefresh() {
      if (!this.isConnected || this.frame != null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.refresh();
      });
    }

    bindGallery() {
      const gallery = this.dataset.mode === 'quote'
        ? this.closest('[data-lvq-gallery]')
        : this.closest('media-gallery');
      if (this.gallery !== gallery) {
        this.observer?.disconnect();
        this.removeReadyMarker();
        this.gallery = gallery;
        this.currentId = null;
        this.lastActiveId = null;
        this.lastThumbnailId = null;
        if (gallery) {
          this.observer = new MutationObserver((records) => {
            // Rendering our counter/buttons must never schedule itself again.
            if (records.some((record) => !this.contains(record.target))) this.scheduleRefresh();
          });
          this.observer.observe(gallery, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: [
              'class', 'aria-current', 'disabled', 'aria-disabled',
              'data-media-id', 'data-target', 'data-lvq-image-id', 'data-lvq-ready',
            ],
          });
        }
      }

      const viewer = this.dataset.mode === 'quote'
        ? gallery?.querySelector('[data-lvq-main-link]')
        : gallery?.querySelector('[id^="GalleryViewer-"]');
      if (this.viewer !== viewer) {
        this.viewer?.removeEventListener('slideChanged', this.onSlideChanged);
        this.viewer = viewer;
        // Dawn's slideChanged event does not bubble.
        this.viewer?.addEventListener('slideChanged', this.onSlideChanged);
      }
    }

    refresh(preferredId) {
      if (!this.isConnected) return;
      this.bindGallery();
      this.previousButton = this.querySelector('[data-lv-gallery-previous]');
      this.nextButton = this.querySelector('[data-lv-gallery-next]');
      this.currentLabel = this.querySelector('[data-lv-gallery-current]');
      this.totalLabel = this.querySelector('[data-lv-gallery-total]');
      if (!this.previousButton || !this.nextButton || !this.currentLabel || !this.totalLabel) return;

      if (this.dataset.mode === 'quote') {
        this.entries = Array.from(this.gallery?.querySelectorAll('[data-lvq-thumbnail]') || [])
          .map((trigger) => ({ id: trigger.dataset.lvqImageId, trigger }));
        this.ready = this.gallery?.dataset.lvqReady === 'true' && !!this.viewer;
      } else {
        const thumbnails = this.gallery?.querySelector('[id^="GalleryThumbnails-"]');
        const triggers = new Map(
          Array.from(thumbnails?.querySelectorAll('[data-target]') || [])
            .map((item) => [item.dataset.target, item.querySelector('button')])
        );
        // Use the current rendered list, not product.media or the thumbnail count.
        // Dawn may add, remove and reorder these items after a variant change.
        this.entries = Array.from(this.viewer?.querySelectorAll('li[data-media-id]') || [])
          .map((media) => ({ id: media.dataset.mediaId, media, trigger: triggers.get(media.dataset.mediaId) }));
        this.ready = typeof this.gallery?.setActiveMedia === 'function'
          && this.gallery.elements?.viewer === this.viewer
          && this.gallery.elements?.thumbnails === thumbnails
          && typeof thumbnails?.isSlideVisible === 'function';
      }

      this.ready = !!this.ready && this.entries.length > 0
        && this.entries.every((entry) => entry.id && entry.trigger);
      const activeId = this.entries.find((entry) => entry.media?.classList.contains('is-active'))?.id;
      const thumbnailId = this.entries.find((entry) => entry.trigger?.getAttribute('aria-current') === 'true')?.id;

      if (preferredId && this.entries.some((entry) => entry.id === preferredId)) {
        this.currentId = preferredId;
      } else if (activeId && activeId !== this.lastActiveId) {
        this.currentId = activeId;
      } else if (thumbnailId && thumbnailId !== this.lastThumbnailId) {
        this.currentId = thumbnailId;
      } else if (!this.entries.some((entry) => entry.id === this.currentId)) {
        this.currentId = thumbnailId || activeId || this.entries[0]?.id;
      }
      // A swipe changes aria-current but Dawn can retain the former is-active
      // class. Compare changes, so that stale class does not undo swipe position.
      this.lastActiveId = activeId;
      this.lastThumbnailId = thumbnailId;
      this.currentIndex = this.entries.findIndex((entry) => entry.id === this.currentId);
      this.updateText(this.currentLabel, this.currentIndex + 1);
      this.updateText(this.totalLabel, this.entries.length);
      this.previousButton.disabled = !this.canActivate(this.currentIndex - 1);
      this.nextButton.disabled = !this.canActivate(this.currentIndex + 1);

      for (const button of [this.previousButton, this.nextButton]) {
        if (this.viewer?.id) {
          if (button.getAttribute('aria-controls') !== this.viewer.id) button.setAttribute('aria-controls', this.viewer.id);
        } else {
          button.removeAttribute('aria-controls');
        }
      }
      this.hidden = !this.ready || this.entries.length < 2;
      if (this.ready) {
        this.setAttribute('data-ready', 'true');
        this.gallery.setAttribute('data-lv-navigation-ready', 'true');
      } else {
        this.removeAttribute('data-ready');
        this.removeReadyMarker();
      }
    }

    removeReadyMarker() {
      if (!this.gallery) return;
      const anotherReadyNavigation = Array.from(this.gallery.querySelectorAll('lv-gallery-navigation[data-ready="true"]'))
        .some((navigation) => navigation !== this && navigation.isConnected);
      if (!anotherReadyNavigation) this.gallery.removeAttribute('data-lv-navigation-ready');
    }

    updateText(element, value) {
      const text = String(value);
      if (element.textContent !== text) element.textContent = text;
    }

    canActivate(index) {
      const trigger = this.entries?.[index]?.trigger;
      return this.ready && !!trigger && !trigger.disabled && trigger.getAttribute('aria-disabled') !== 'true';
    }

    activate(index) {
      if (!this.canActivate(index)) return;
      const trigger = this.entries[index].trigger;
      if (this.dataset.mode === 'quote') {
        // Run lakeview-quote.js's existing handler, including its zoom URL and
        // image metadata updates. Never follow the anchor if its handler fails.
        const click = new MouseEvent('click', { bubbles: true, cancelable: true, button: 0, view: window });
        click.preventDefault();
        trigger.dispatchEvent(click);
      } else {
        trigger.click();
      }
      this.refresh();
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (button !== this.previousButton && button !== this.nextButton) return;
      // A variant response might have changed the list since the last frame.
      this.refresh();
      this.activate(this.currentIndex + (button === this.previousButton ? -1 : 1));
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.refresh();
      const rtl = getComputedStyle(this).direction === 'rtl';
      const step = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : (rtl ? 1 : -1);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.entries.length - 1 : this.currentIndex + step;
      this.activate(index);
    }

    onSlideChanged(event) {
      this.refresh(event.detail?.currentElement?.dataset.mediaId);
    }
  }

  customElements.define('lv-gallery-navigation', LakeviewGalleryNavigation);
})();
